/**
 * runidea.ai — Phase 14 Wave 3 — Announcement banner + maintenance overlay.
 *
 * Mounted in AppShell. Polls `/api/announcements/active` every 5 min,
 * renders the highest-severity active announcement at the very top of
 * the page. Dismissible announcements remember their dismiss state in
 * localStorage keyed by announcement id.
 *
 * Also exports `useMaintenanceWatch` — a hook that listens for HTTP 503
 * `{maintenance: true}` responses from axios and surfaces a full-page
 * maintenance overlay. (axios interceptor wired in `lib/api.js`.)
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { AlertTriangle, Info, AlertCircle, X } from "lucide-react";

const POLL_MS = 5 * 60_000;
const STORAGE_PREFIX = "runidea.announce.dismissed:";

const LEVEL_STYLES = {
  info: {
    Icon: Info,
    cls: "bg-blue-600 text-white",
    textCls: "text-blue-50",
  },
  warning: {
    Icon: AlertCircle,
    cls: "bg-amber-500 text-white",
    textCls: "text-amber-50",
  },
  critical: {
    Icon: AlertTriangle,
    cls: "bg-red-600 text-white",
    textCls: "text-red-50",
  },
};

function dismissedKey(id) {
  return `${STORAGE_PREFIX}${id}`;
}

export default function AnnouncementBanner() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/announcements/active");
        if (alive) setItems(data?.items || []);
      } catch {
        // silent — banner is non-critical
      }
    };
    load();
    const t = setInterval(load, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Filter out user-dismissed.
  const visible = items.filter((a) => {
    if (!a.dismissible) return true;
    try { return !window.localStorage?.getItem(dismissedKey(a.id)); }
    catch { return true; }
  });
  if (visible.length === 0) return null;

  // Show only the highest-severity active announcement on screen at once.
  const order = { critical: 0, warning: 1, info: 2 };
  visible.sort((a, b) => (order[a.level] ?? 9) - (order[b.level] ?? 9));
  const a = visible[0];
  const meta = LEVEL_STYLES[a.level] || LEVEL_STYLES.info;
  const Icon = meta.Icon;

  const dismiss = () => {
    try { window.localStorage?.setItem(dismissedKey(a.id), "1"); } catch { /* ignore */ }
    setItems(items.filter((x) => x.id !== a.id));
  };

  return (
    <div
      data-testid="announcement-banner"
      data-level={a.level}
      role="status"
      className={`flex items-center justify-between gap-3 px-4 py-2 text-sm font-medium shadow-sm ${meta.cls}`}
    >
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 flex-shrink-0" />
        <span className={meta.textCls}>{a.message}</span>
      </div>
      {a.dismissible ? (
        <button
          type="button"
          onClick={dismiss}
          data-testid="announcement-dismiss"
          aria-label="Dismiss"
          className="rounded-md p-1 hover:bg-white/15"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );
}
